import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { VideoPayload } from '@data';

const LIVE_ANALYSIS_ACTIVE_STATUSES: string[] = [
    'LIVE_FEEDBACK_WAITING_FOR_BALL',
    'LIVE_FEEDBACK_WAITING_FOR_P1',
    'LIVE_FEEDBACK_ACTIVE',
];

const LIVE_ANALYSIS_SETUP_STATUSES: string[] = ['LIVE_FEEDBACK_WAITING_FOR_CONFIG', 'LIVE_FEEDBACK_WAITING_FOR_TARE'];

const LIVE_ANALYSIS_STATUSES: string[] = [...LIVE_ANALYSIS_SETUP_STATUSES, ...LIVE_ANALYSIS_ACTIVE_STATUSES];

const BALL_LESS_STATUESES: string[] = ['BALLLESS_WAITING_FOR_P1', 'BALLLESS_STEADY', 'BALLLESS_SWING_IN_MOTION'];

export const REFLECTED_STATUSES: string[] = [
    'WAITING_FOR_BALL',
    'ANALYZING_SWING',
    'BALL_DETECTED',
    'BALL_STEADY',
    'SWING_ANALYSIS_FAILED',
    'IDLE',
    'INIT',
    ...BALL_LESS_STATUESES,
    ...LIVE_ANALYSIS_STATUSES,
];

export type ReflectedStatus = (typeof REFLECTED_STATUSES)[number];

export type LiveAnalysisMessage = LiveMeasurementMessage | LiveCountDownMessage | LivePositionStateMessage;

const LIVE_MEASUREMENT_MESSAGE_TYPES = {
    LM: 'LM',
    POSITION: 'POSITION',
    COUNT_DOWN: 'COUNT_DOWN',
    POS_STATE: 'POS_STATE',
} as const;

export type LiveMeasurementMessage = {
    type: typeof LIVE_MEASUREMENT_MESSAGE_TYPES.LM;
    measurements: {
        par: number[];
        val: number;
        r: number;
    }[];
};

export type LivePositionStateMessage = {
    type: typeof LIVE_MEASUREMENT_MESSAGE_TYPES.POS_STATE;
    position: string;
    tareNum: number;
    parameters: Record<string, number[]>;
    // addition: Record<string, [number, boolean]>;
};

export type LiveCountDownMessage = {
    type: typeof LIVE_MEASUREMENT_MESSAGE_TYPES.COUNT_DOWN;
    countDown: number;
};

export const beastStatusAtom = atom<ReflectedStatus>('IDLE');
export const isAnalyzingAtom = atom<boolean>(false);
export const isConnectedToBeastAtom = atom<boolean>(false);
export const instantReplayActiveSwingIdAtom = atomWithReset<string | null>(null);
export const instantReplayActiveVideosAtom = atomWithReset<VideoPayload[] | null>(null);
export const liveFeedbackActiveAtom = atom<boolean>((get) => LIVE_ANALYSIS_STATUSES.includes(get(beastStatusAtom)));

export const liveFeedbackWaitingToStartAtom = atom<boolean>((get) =>
    LIVE_ANALYSIS_SETUP_STATUSES.includes(get(beastStatusAtom)),
);

export const liveFeedbackStartedAtom = atom<boolean>((get) =>
    LIVE_ANALYSIS_ACTIVE_STATUSES.includes(get(beastStatusAtom)),
);

export const liveFeedbackMeasurementsAtom = atom<LiveMeasurementMessage | null>(null);
export const liveFeedbackPositionStateAtom = atom<LivePositionStateMessage | null>(null);
export const liveFeedbackCountDownAtom = atom<LiveCountDownMessage | null>(null);

export const dispatchLiveFeedbackAtom = atom(null, (_get, set, payload: LiveAnalysisMessage | null) => {
    if (!payload) {
        set(liveFeedbackMeasurementsAtom, null);
        set(liveFeedbackPositionStateAtom, null);
        set(liveFeedbackCountDownAtom, null);
        return;
    }

    switch (payload.type) {
        case LIVE_MEASUREMENT_MESSAGE_TYPES.LM:
            set(liveFeedbackMeasurementsAtom, payload);
            break;
        case LIVE_MEASUREMENT_MESSAGE_TYPES.POS_STATE:
            set(liveFeedbackPositionStateAtom, payload);
            break;
        case LIVE_MEASUREMENT_MESSAGE_TYPES.COUNT_DOWN:
            set(liveFeedbackCountDownAtom, payload);
            break;
        default:
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.error('unknown message type:', (payload as any)?.type);
    }
});
